'use client';

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchCRMQuery } from 'src/api/redux';
import { useDebounce } from 'use-debounce';
import { Controller } from 'react-hook-form';
import {
  Typography,
  CircularProgress,
  Input,
  ClickAwayListener,
} from 'src/components/PulseUI';

interface CRMLookupProps {
  crmType: string;
  crm: string;
  option: MedScout.UserInputOption;
  control: any;
  rules?: any;
}

const CRMLookup = ({
  crmType,
  crm,
  option,
  control,
  rules = [],
}: CRMLookupProps) => {
  const [search, setSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [open, setOpen] = useState(false);

  const [searchTerm] = useDebounce(search, 500);
  const {
    data: searchCRM,
    isLoading,
    isFetching,
  } = useSearchCRMQuery(
    {
      crmType,
      q: searchTerm,
      option: option?.id,
      crm,
    },
    {
      skip: !crmType || !option || !crm || searchTerm.length <= 2,
    }
  );

  // Force open dropdown when search results are available or when there's a valid search with no results
  useEffect(() => {
    if (searchTerm.length > 2) {
      // Open dropdown for valid searches (with or without results)
      setOpen(true);
    }
  }, [searchCRM, searchTerm]);

  // Handle click away properly
  const handleClickAway = () => {
    // Only close if dropdown is open
    if (open) {
      setOpen(false);
    }
  };

  const loading = isLoading || isFetching;

  // Reference to the input element for positioning the dropdown
  const inputRef = useRef<HTMLDivElement>(null);

  return (
    <Controller
      name={option.crm_api_code}
      control={control}
      rules={rules}
      render={({ field }) => (
        <ClickAwayListener onClickAway={handleClickAway}>
          <div className="relative w-full" data-testid="crm-lookup">
            {/* Input field */}
            <div ref={inputRef}>
              <Input
                fullWidth
                variant="plain"
                placeholder={selectedOption ? selectedOption.label : 'Search...'}
                value={selectedOption ? selectedOption.label : search}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setSearch(newValue);
                  if (newValue.length >= 3) {
                    setOpen(true);
                  } else if (newValue.length === 0) {
                    setSelectedOption(null);
                    field.onChange('');
                  }
                }}
                onFocus={() => {
                  if (search.length >= 3) {
                    setOpen(true);
                  }
                }}
                onClick={() => {
                  if (search.length >= 3) {
                    setOpen(true);
                  }
                }}
                className="cursor-pointer"
                aria-label={`Search for ${option.attribute_code}`}
                data-testid="crm-lookup-input"
              />
            </div>

            {/* Custom dropdown - rendered in a Portal to appear above dialog */}
            {open && searchTerm.length >= 3 &&
              createPortal(
                <div
                  className="fixed bg-white shadow-lg rounded border border-neutral-200 max-h-[250px] overflow-auto z-[9999999] text-sm"
                  style={{
                    width: inputRef.current
                      ? inputRef.current.offsetWidth
                      : 'auto',
                    top: inputRef.current
                      ? inputRef.current.getBoundingClientRect().bottom +
                        window.scrollY +
                        4
                      : 0,
                    left: inputRef.current
                      ? inputRef.current.getBoundingClientRect().left +
                        window.scrollX
                      : 0,
                  }}
                  data-portal-content="true"
                  data-testid="crm-lookup-dropdown"
                >
                  {loading ? (
                    <div className="p-2 text-center">
                      <CircularProgress size="sm" />
                    </div>
                  ) : searchCRM && searchCRM.length > 0 ? (
                    <div className="flex flex-col" role="listbox" aria-label="Search results">
                      {searchCRM.map((item) => (
                        <button
                          type="button"
                          key={item.value}
                          onClick={() => {
                            field.onChange(item.value);
                            setSelectedOption(item);
                            setOpen(false);
                          }}
                          className="py-2 px-3 text-left text-sm hover:bg-neutral-50 hover:border-l-[3px] hover:border-l-teal-400 transition-colors cursor-pointer"
                          role="option"
                          aria-selected={false}
                          data-testid={`crm-lookup-option-${item.value}`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  ) : search.length >= 3 ? (
                    <div className="p-2 text-center">
                      <Typography variant="body2" color="secondary">
                        No results found. Please try a different search term.
                      </Typography>
                    </div>
                  ) : (
                    <div className="p-2 text-center">
                      <Typography variant="body2" color="secondary">
                        {search.length > 0
                          ? 'Please enter at least 3 characters'
                          : 'Start typing to search'}
                      </Typography>
                    </div>
                  )}
                </div>,
                document.body
              )}
          </div>
        </ClickAwayListener>
      )}
    />
  );
};

export default CRMLookup;
